import React, { useState } from "react";
import { BarChart3 } from "lucide-react";

import "@/features/visao-geral/visaoGeral.css";

import { faixaDoPico } from "../regras";

/**
 * Quadro de calor hora a hora no modelo do mapa de calor da Visão Geral (dono,
 * 27/10/2026). Usado no faturamento por módulo (Resumo) e no mapa mesa × hora
 * (Mesas). Cor pela faixa do pico: alta ≥85%, mediana ≥50%, baixa ≥30%, muito
 * baixa abaixo. Hora sem valor em nenhuma linha vira uma coluna estreita.
 *
 * horas:   [{ hora, rotulo }] em ordem, sem buracos
 * linhas:  [{ key, nome, rotulo?, valores: {hora: n}, contagens?: {hora: n}, total }]
 * total:   linha "Total" no mesmo formato (opcional)
 * escala:  "linha" = cor pelo pico de cada linha; "geral" = pelo pico do quadro inteiro
 * celula(valor) → texto da célula · totalCelula(linha) → { valor, sub? }
 * dica(linha, h) / dicaTotal(linha) → { sub, itens: [[rótulo, valor]] }
 * barras:  { valores: {hora: n}, rotulo, total, dica(h) } — barras embaixo (opcional)
 * legenda: começo da legenda · vazio: texto da coluna sem valor
 */
const LEGENDA = [
  ["alta", "Alta", "85% do pico ou mais"],
  ["media", "Mediana", "50% a 85%"],
  ["baixa", "Baixa", "30% a 50%"],
  ["mbaixa", "Muito baixa", "abaixo de 30%"],
];
const num = (v) => Number(v) || 0;
// A coluna Total fica parada à direita quando o quadro rola de lado (tela estreita)
const FIXA_DIREITA = "sticky right-0 z-[1] bg-[var(--vg-surface)]";
// A coluna do nome fica do tamanho do nome; a largura que sobra vai para as horas
const NOME = { width: "1%" };
const p2 = (h) => String(h).padStart(2, "0");
export const faixaHora = (h) => `${p2(h.hora)}h às ${p2((h.hora + 1) % 24)}h`;

function montarColunas(horas, temValor) {
  const cols = [];
  horas.forEach((h) => {
    if (temValor(h.hora)) { cols.push({ tipo: "h", ...h }); return; }
    const ult = cols[cols.length - 1];
    if (ult?.tipo === "gap") ult.ate = h.hora;
    else cols.push({ tipo: "gap", de: h.hora, ate: h.hora });
  });
  return cols;
}

export default function QuadroCalor({
  horas, linhas, total, escala = "linha", celula, totalCelula, dica, dicaTotal, barras, legenda, vazio = "sem vendas", e2e,
}) {
  const [aberta, setAberta] = useState(null);
  const todas = total ? [...linhas, total] : linhas;
  const cols = montarColunas(horas, (h) => todas.some((l) => num(l.valores[h]) > 0));
  const visiveis = cols.filter((c) => c.tipo === "h");
  if (!visiveis.length || !linhas.length) return null;

  const picoDe = (l) => Math.max(0, ...visiveis.map((h) => num(l.valores[h.hora])));
  const picoGeral = Math.max(0, ...linhas.map(picoDe));
  const pico = (l) => (escala === "geral" && l !== total ? picoGeral : picoDe(l));
  const maxBarra = barras ? Math.max(1, ...visiveis.map((h) => num(barras.valores[h.hora]))) : 1;

  const mostrar = (e, titulo, info) => {
    const r = e.currentTarget.getBoundingClientRect();
    setAberta({ x: r.left + r.width / 2, y: r.top, titulo, ...info });
  };
  const evDica = (titulo, info) => (info
    ? { onMouseEnter: (e) => mostrar(e, titulo, info), onMouseLeave: () => setAberta(null) }
    : {});

  const td = (l, h, picoLinha) => {
    const v = num(l.valores[h.hora]);
    const f = faixaDoPico(v, picoLinha, l.contagens ? num(l.contagens[h.hora]) : null);
    return (
      <td key={h.hora} className={`c ${f.faixa} min-w-[60px] !w-auto`} {...evDica(faixaHora(h), v > 0 ? dica(l, h, f) : null)}>
        <b className="!text-[12px] whitespace-nowrap">{v > 0 ? celula(v, l) : "–"}</b>
      </td>
    );
  };

  let primeira = true;
  const nLinhas = todas.length + (barras ? 1 : 0);
  const linha = (l, extra = "") => {
    const picoLinha = pico(l);
    const tds = cols.map((c) => {
      if (c.tipo === "h") return td(l, c, picoLinha);
      if (!primeira) return null;
      return (
        <td key={`g${c.de}`} className="gap" rowSpan={nLinhas}>
          <span>{c.de === c.ate ? `${p2(c.de)}h` : `${p2(c.de)}h a ${p2(c.ate)}h`}, {vazio}</span>
        </td>
      );
    });
    primeira = false;
    const tot = totalCelula(l);
    return (
      <tr key={l.key} className={extra}>
        <th className="rl" scope="row" style={NOME}>{l.rotulo || l.nome}</th>
        {tds}
        <td className={`tot min-w-[84px] ${FIXA_DIREITA}`} {...evDica(`Total de ${l.nome}`, dicaTotal?.(l))}>
          <b className="whitespace-nowrap">{tot.valor}</b>
          {tot.sub && <i>{tot.sub}</i>}
        </td>
      </tr>
    );
  };

  return (
    <div className="vg vg-heat-wrap" data-e2e={e2e}>
      <div className="vg-scroller">
        <table className="vg-heat" onClick={() => setAberta(null)}>
          <thead>
            <tr>
              <th className="rl" style={NOME} />
              {cols.map((c) => (c.tipo === "h"
                ? <th key={c.hora}>{c.rotulo}</th>
                : <th key={`g${c.de}`} title={vazio}>···</th>))}
              <th className={FIXA_DIREITA}>Total</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => linha(l))}
            {total && linha(total, "linhatot")}
            {barras && (
              <tr>
                <th className="rl" title={barras.rotulo}>
                  <span className="hidden sm:block text-[12px] font-semibold">{barras.rotulo}<small>por hora</small></span>
                  <BarChart3 className="sm:hidden w-5 h-5 mx-auto text-muted-foreground" />
                </th>
                {visiveis.map((h) => (
                  <td key={h.hora} className="hb-cell" {...evDica(faixaHora(h), barras.dica(h))}>
                    <div className="hb">
                      <i className="sold" style={{ height: `${Math.round((num(barras.valores[h.hora]) / maxBarra) * 100)}%` }} />
                    </div>
                  </td>
                ))}
                <td className={`hb-tot ${FIXA_DIREITA}`}><b>{barras.total}</b></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-[11px] text-muted-foreground" data-e2e="legenda-mapa">
        <span className="font-semibold text-foreground/80">{legenda}</span>
        {LEGENDA.map(([k, nome, faixa]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <i className="w-3 h-3 rounded-[3px] inline-block" style={{ background: `var(--${k})` }} />
            <b className="font-semibold text-foreground/80">{nome}</b> {faixa}
          </span>
        ))}
      </div>
      {aberta && (
        <div className="vg-tip fixed" style={{ left: Math.max(8, Math.min(window.innerWidth - 240, aberta.x - 115)), top: Math.max(8, aberta.y - 12 - 34 - aberta.itens.length * 22) }}>
          <h4>{aberta.titulo} <small>{aberta.sub}</small></h4>
          {aberta.itens.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
        </div>
      )}
    </div>
  );
}
